import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Menu,
  Lock,
  Plus,
  LogOut,
  Shield,
  Building2,
  ArrowLeft,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useUnreadNotificationCount } from '@/hooks/queries/useNotificationQueries';
import { APP_ROUTES } from '@/config/routes.config';
import { cn } from '@/lib/utils';

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
  isGated?: boolean;
  isNew?: boolean;
  onClick?: () => void;
}

const NavItem = ({ to, icon: Icon, label, badge, isGated, isNew, onClick }: NavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      onClick={onClick}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        "flex items-center justify-between px-4 py-3 rounded-lg transition-colors",
        "focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none",
        isActive
          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100 border-l-4 border-emerald-600'
          : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground'
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className={cn("h-5 w-5", isActive && "text-emerald-600 dark:text-emerald-400")} aria-hidden="true" />
        <span className={cn("font-medium", isActive && "text-emerald-900 dark:text-emerald-100")}>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {isNew && (
          <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] px-1.5 py-0" aria-label="New feature">
            NEW
          </Badge>
        )}
        {badge !== undefined && badge > 0 && (
          <Badge variant="destructive" className="ml-2" aria-label={`${badge} unread`}>
            {badge}
          </Badge>
        )}
        {isGated && (
          <Lock className="h-3 w-3 text-muted-foreground" aria-label="Premium feature" />
        )}
      </div>
    </Link>
  );
};

export const AuthenticatedMobileMenu = () => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: unreadCount } = useUnreadNotificationCount();
  const { isAdmin, isSuperAdmin } = useAdminAccess();
  const { can } = usePermissions();
  const { canAccess } = useFeatureAccess();

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
    navigate('/auth');
  };

  const closeMenu = () => setOpen(false);
  
  // Check if currently on admin route
  const location = useLocation();
  const isOnAdminRoute = location.pathname.startsWith('/admin');

  // Filter routes based on permissions and features
  const filteredRoutes = useMemo(() => {
    return APP_ROUTES.filter(route => {
      // Check role requirements
      if (route.requiresRole && user?.roles) {
        const hasRole = route.requiresRole.some(role => user.roles?.includes(role));
        if (!hasRole) return false;
      }

      // Always show, but may have gate indicator
      return true;
    });
  }, [user]);

  // Group routes
  const mainRoutes = filteredRoutes.filter(r => r.group === 'main' || !r.group);
  const accountRoutes = filteredRoutes.filter(r => r.group === 'account');

  // Get tier info
  const tierName = user?.organization?.subscription_tier || 'Starter';
  const billingState = user?.organization?.billing_state;

  if (!isMobile) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0">
        <SheetHeader className="px-4 py-6 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <LucideIcons.User className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 text-left">
              <SheetTitle className="text-sm font-semibold">
                {user?.profile.first_name || user?.email?.split('@')[0] || 'User'}
              </SheetTitle>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-muted-foreground">{user?.profile.organization_name || 'Organization'}</p>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs px-1.5 py-0",
                    billingState === 'active' ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-slate-500/10"
                  )}
                >
                  {tierName}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-120px)] overflow-y-auto">
          <div className="flex-1 py-4">
            {/* Back to App (when on admin routes) */}
            {isOnAdminRoute && (
              <div className="px-2 mb-4">
                <Link
                  to="/app/dashboard"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all group focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none"
                  aria-label="Return to App Dashboard"
                >
                  <ArrowLeft className="h-5 w-5 text-emerald-600 dark:text-emerald-400 group-hover:translate-x-[-2px] transition-transform" aria-hidden="true" />
                  <span className="font-medium text-emerald-700 dark:text-emerald-300">Back to App Dashboard</span>
                </Link>
              </div>
            )}

            {/* Quick Actions */}
            {can('recipes', 'create') && (
              <div className="px-2 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    className="w-full justify-start bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      navigate('/app/recipes/new');
                      closeMenu();
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Recipe
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      navigate('/app/menu-planning');
                      closeMenu();
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Menu
                  </Button>
                </div>
              </div>
            )}

            {/* Main Navigation */}
            <div className="px-2 mb-4">
              <p 
                className="px-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                id="mobile-main-nav-heading"
              >
                Main
              </p>
              <nav className="space-y-1" role="navigation" aria-labelledby="mobile-main-nav-heading">
                {mainRoutes.map(route => {
                  const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.Circle;
                  const isGated = route.requiresFeature && !canAccess(route.requiresFeature, 'view').enabled;
                  const badge = route.path === '/app/notifications' ? unreadCount : undefined;
                  
                  return (
                    <NavItem
                      key={route.path}
                      to={route.path}
                      icon={IconComponent}
                      label={route.label}
                      badge={badge}
                      isGated={isGated}
                      onClick={closeMenu}
                    />
                  );
                })}
              </nav>
            </div>

            <Separator className="my-4" />

            {/* Account Section */}
            <div className="px-2 mb-4">
              <p 
                className="px-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                id="mobile-account-nav-heading"
              >
                Account
              </p>
              <nav className="space-y-1" role="navigation" aria-labelledby="mobile-account-nav-heading">
                {accountRoutes.map(route => {
                  const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.Circle;
                  
                  return (
                    <NavItem
                      key={route.path}
                      to={route.path}
                      icon={IconComponent}
                      label={route.label}
                      onClick={closeMenu}
                    />
                  );
                })}
              </nav>
            </div>

            {/* Admin Section (Conditional) */}
            {isAdmin && (
              <>
                <Separator className="my-4" />
                <div className="px-2 mb-4">
                  <div className="flex items-center gap-2 px-4 mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Admin Portal
                    </p>
                    {isSuperAdmin ? (
                      <Badge variant="outline" className="text-xs bg-purple-500/20 text-purple-700 border-purple-500/30">
                        Super Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-red-500/20 text-red-700 border-red-500/30">
                        Admin
                      </Badge>
                    )}
                  </div>
                  <nav className="space-y-1">
                    <NavItem to="/admin/dashboard" icon={Shield} label="Dashboard" onClick={closeMenu} />
                    <NavItem to="/admin/users" icon={Shield} label="Users" onClick={closeMenu} />
                    <NavItem to="/admin/sysco" icon={LucideIcons.Package} label="Sysco Hub" isNew onClick={closeMenu} />
                    <NavItem to="/admin/billing" icon={LucideIcons.CreditCard} label="Billing" onClick={closeMenu} />
                    <NavItem to="/admin/reports" icon={LucideIcons.BarChart3} label="Reports" onClick={closeMenu} />
                    <NavItem to="/admin/support" icon={LucideIcons.LifeBuoy} label="Support" onClick={closeMenu} />
                    <NavItem to="/admin/settings" icon={LucideIcons.Settings} label="Settings" onClick={closeMenu} />
                  </nav>
                </div>
              </>
            )}
            
            {/* Enterprise Section (Super Admin Only) */}
            {isSuperAdmin && (
              <>
                <Separator className="my-4" />
                <div className="px-2 mb-4">
                  <div className="flex items-center gap-2 px-4 mb-2">
                    <Building2 className="h-3 w-3 text-purple-600" />
                    <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">
                      Enterprise
                    </p>
                  </div>
                  <nav className="space-y-1">
                    <NavItem to="/admin/enterprise/overview" icon={LucideIcons.Home} label="Overview" onClick={closeMenu} />
                    <NavItem to="/admin/enterprise/organizations" icon={Building2} label="Organizations" onClick={closeMenu} />
                    <NavItem to="/admin/enterprise/users" icon={Shield} label="All Users" onClick={closeMenu} />
                    <NavItem to="/admin/enterprise/security" icon={Shield} label="Security" onClick={closeMenu} />
                  </nav>
                </div>
              </>
            )}
          </div>

          {/* Sign Out Footer */}
          <div className="px-2 py-4 border-t bg-muted/30">
            <Button
              variant="ghost"
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
